import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPhone, isPhoneValid, phoneDigits } from '@/utils/phone';
import { updatePhone, recordSmsConsent } from '@/services/phoneSmsPreferences';

const SMS_DISCLOSURE =
  'I agree to receive SMS training reminders from CGM Sports. ' +
  'Message frequency varies. Msg & data rates may apply. Reply HELP for help, ' +
  'STOP to unsubscribe. We will never share your phone number with third parties.';

interface PhoneSmsSettingsProps {
  userId: number;
  initialPhone?: string;
  initialSmsOptIn?: boolean;
}

const PhoneSmsSettings: React.FC<PhoneSmsSettingsProps> = ({
  userId,
  initialPhone = '',
  initialSmsOptIn = false,
}) => {
  const [phone, setPhone] = useState(formatPhone(initialPhone));
  const [smsOptIn, setSmsOptIn] = useState(initialSmsOptIn);
  const [savedPhone, setSavedPhone] = useState(initialPhone);
  const [savedOptIn, setSavedOptIn] = useState(initialSmsOptIn);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    if (!formatted) setSmsOptIn(false);
    setSuccess(null);
    setError(null);
  };

  const handleOptInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSmsOptIn(e.target.checked);
    setSuccess(null);
    setError(null);
  };

  const persistUserInfo = (next: { phone: string; sms_opt_in: boolean }) => {
    const stored = localStorage.getItem('user_info');
    if (!stored) return;
    const parsed = JSON.parse(stored);
    parsed.phone = next.phone;
    parsed.sms_opt_in = next.sms_opt_in;
    localStorage.setItem('user_info', JSON.stringify(parsed));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (phone && !isPhoneValid(phone)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (smsOptIn && !phone) {
      setError('Add a phone number before opting in to SMS.');
      return;
    }

    setSaving(true);
    try {
      const digits = phoneDigits(phone);
      let nextOptIn = savedOptIn;

      if (digits !== savedPhone) {
        const result = await updatePhone(digits);
        nextOptIn = result.sms_opt_in;
        setSavedPhone(result.phone);
      }

      if (smsOptIn !== nextOptIn) {
        if (smsOptIn && !digits) {
          throw new Error('Cannot opt in without a phone number.');
        }
        await recordSmsConsent(
          userId,
          smsOptIn ? 'granted' : 'revoked',
          smsOptIn ? SMS_DISCLOSURE : 'opt_out_from_settings',
        );
        nextOptIn = smsOptIn;
      }

      setSavedOptIn(nextOptIn);
      setSmsOptIn(nextOptIn);
      persistUserInfo({ phone: digits, sms_opt_in: nextOptIn });
      setSuccess('Saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save phone/SMS preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="settings-phone" className="block text-sm font-medium cgm-text-secondary mb-1">
          Phone Number <span className="cgm-text-muted text-xs font-normal">(optional)</span>
        </label>
        <input
          id="settings-phone"
          type="tel"
          value={phone}
          onChange={handlePhoneChange}
          disabled={saving}
          className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder="(555) 123-4567"
        />
      </div>

      {phone && (
        <label htmlFor="settings-sms-opt-in" className="flex items-start gap-2 cursor-pointer select-none">
          <input
            id="settings-sms-opt-in"
            type="checkbox"
            checked={smsOptIn}
            onChange={handleOptInChange}
            disabled={saving}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-xs cgm-text-muted leading-relaxed">
            {SMS_DISCLOSURE} See{' '}
            <Link to="/privacy" className="underline">Privacy Policy</Link> and{' '}
            <Link to="/terms" className="underline">Terms</Link>.
          </span>
        </label>
      )}

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
      )}
      {success && (
        <div className="text-xs text-green-600 dark:text-green-400">{success}</div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default PhoneSmsSettings;
